import React, { useState, useContext, useMemo } from 'react';
import { AppContext } from '../contexts/AppContext';
import { useUI } from '../contexts/UIContext';
import { Deuda, MedioDePago, Rol } from '../types';
import Modal from '../components/Modal';

type Filtro = 'pendientes' | 'pagadas' | 'canceladas' | 'todas';

const DeudasScreen: React.FC = () => {
    const { user, deudas, socios, sesionCaja, cobrarDeudas, cancelarDeuda, isReadOnly } = useContext(AppContext);
    const ui = useUI();

    const [filtro, setFiltro] = useState<Filtro>('pendientes');
    const [busqueda, setBusqueda] = useState('');
    const [socioExpandido, setSocioExpandido] = useState<string | number | null>(null);
    const [cobrarModal, setCobrarModal] = useState<{ socioId: string | number; ids: number[]; total: number } | null>(null);
    const [medioDePago, setMedioDePago] = useState<MedioDePago>(MedioDePago.EFECTIVO);
    const [procesando, setProcesando] = useState(false);

    const canManage = (user?.rol === Rol.ADMIN || user?.rol === Rol.CAJERO) && !isReadOnly;
    const canCancel = user?.rol === Rol.ADMIN && !isReadOnly;

    // Aplicar filtro de estado
    const deudasFiltradas = useMemo(() => {
        if (filtro === 'todas') return deudas;
        const mapa: Record<Filtro, Deuda['estado'] | null> = {
            pendientes: 'pendiente',
            pagadas: 'pagada',
            canceladas: 'cancelada',
            todas: null,
        };
        const target = mapa[filtro];
        return deudas.filter(d => d.estado === target);
    }, [deudas, filtro]);

    // Agrupar por socio
    const porSocio = useMemo(() => {
        const map = new Map<string, { socioId: string | number; nombre: string; deudas: Deuda[]; total: number }>();
        for (const d of deudasFiltradas) {
            const key = String(d.socio_id);
            const socio = d.socio || socios.find(s => String(s.id) === key);
            const nombre = socio ? `${socio.nombre} ${socio.apellido || ''}`.trim() : '(socio desconocido)';
            const entry = map.get(key) || { socioId: d.socio_id, nombre, deudas: [], total: 0 };
            entry.deudas.push(d);
            // Solo sumamos pendientes al total a cobrar
            if (d.estado === 'pendiente') entry.total += Number(d.monto);
            map.set(key, entry);
        }
        let arr = Array.from(map.values());
        // Filtro por nombre
        if (busqueda.trim()) {
            const q = busqueda.toLowerCase();
            arr = arr.filter(e => e.nombre.toLowerCase().includes(q));
        }
        // Ordenar: con total pendiente arriba, después por nombre
        arr.sort((a, b) => b.total - a.total || a.nombre.localeCompare(b.nombre));
        return arr;
    }, [deudasFiltradas, socios, busqueda]);

    const totales = useMemo(() => {
        const sociosConDeuda = porSocio.filter(s => s.total > 0).length;
        const totalAdeudado = porSocio.reduce((s, e) => s + e.total, 0);
        return { sociosConDeuda, totalAdeudado };
    }, [porSocio]);

    const abrirCobrarUna = (deuda: Deuda) => {
        setCobrarModal({ socioId: deuda.socio_id, ids: [deuda.id], total: Number(deuda.monto) });
        setMedioDePago(MedioDePago.EFECTIVO);
    };

    const abrirCobrarTodas = (socioId: string | number, deudasPend: Deuda[]) => {
        const ids = deudasPend.filter(d => d.estado === 'pendiente').map(d => d.id);
        const total = deudasPend.filter(d => d.estado === 'pendiente').reduce((s, d) => s + Number(d.monto), 0);
        if (ids.length === 0) return;
        setCobrarModal({ socioId, ids, total });
        setMedioDePago(MedioDePago.EFECTIVO);
    };

    const confirmarCobro = async () => {
        if (!cobrarModal) return;
        setProcesando(true);
        const res = await cobrarDeudas(cobrarModal.ids, medioDePago);
        setProcesando(false);
        if (res.success) {
            ui.showToast('success', `Cobro registrado: $${cobrarModal.total.toLocaleString('es-AR')}`);
            setCobrarModal(null);
        } else {
            ui.showModal('Error', res.message || 'No se pudo cobrar la deuda.');
        }
    };

    const handleCancelar = async (d: Deuda) => {
        const ok = window.confirm(`¿Cancelar (perdonar) esta deuda de $${Number(d.monto).toLocaleString('es-AR')}?`);
        if (!ok) return;
        const res = await cancelarDeuda(d.id);
        if (!res.success) {
            ui.showModal('Error', res.message || 'No se pudo cancelar la deuda.');
        } else {
            ui.showToast('success', 'Deuda cancelada.');
        }
    };

    return (
        <div className="bg-white rounded-lg shadow-md">
            <div className="p-4 md:p-6 border-b">
                <div className="flex flex-wrap gap-4 justify-between items-start">
                    <div>
                        <h2 className="text-2xl font-bold text-gray-800">Deudas de Socios</h2>
                        <p className="text-sm text-gray-500">Identificá y cobrá deudas pendientes desde Caja.</p>
                    </div>
                    <div className="flex gap-4 text-sm">
                        <div className="bg-red-50 border border-red-200 rounded px-3 py-2">
                            <div className="text-xs text-red-700 font-semibold uppercase">Socios con deuda</div>
                            <div className="text-xl font-bold text-red-700">{totales.sociosConDeuda}</div>
                        </div>
                        <div className="bg-yellow-50 border border-yellow-200 rounded px-3 py-2">
                            <div className="text-xs text-yellow-700 font-semibold uppercase">Total a cobrar</div>
                            <div className="text-xl font-bold text-yellow-700">${totales.totalAdeudado.toLocaleString('es-AR')}</div>
                        </div>
                    </div>
                </div>
                <div className="flex flex-wrap gap-3 mt-4">
                    <input
                        type="text"
                        placeholder="Buscar socio..."
                        value={busqueda}
                        onChange={e => setBusqueda(e.target.value)}
                        className="flex-grow sm:flex-grow-0 sm:w-72 p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <div className="flex gap-1">
                        {(['pendientes', 'pagadas', 'canceladas', 'todas'] as Filtro[]).map(f => (
                            <button
                                key={f}
                                onClick={() => setFiltro(f)}
                                className={`px-3 py-2 text-sm rounded border capitalize ${filtro === f ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-700 hover:bg-gray-50'}`}
                            >
                                {f}
                            </button>
                        ))}
                    </div>
                </div>
            </div>

            <div className="divide-y divide-gray-200">
                {porSocio.length === 0 && (
                    <div className="p-8 text-center text-gray-500">
                        No hay deudas {filtro !== 'todas' ? filtro : ''} para mostrar.
                    </div>
                )}
                {porSocio.map(entry => {
                    const isOpen = String(socioExpandido) === String(entry.socioId);
                    const pendientes = entry.deudas.filter(d => d.estado === 'pendiente');
                    return (
                        <div key={String(entry.socioId)} className="px-4 md:px-6 py-4">
                            <div className="flex flex-wrap items-center justify-between gap-3">
                                <button
                                    onClick={() => setSocioExpandido(isOpen ? null : entry.socioId)}
                                    className="flex items-center gap-2 text-left flex-1 min-w-0"
                                >
                                    <svg className={`w-5 h-5 text-gray-500 transform transition-transform ${isOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"/></svg>
                                    <div className="min-w-0">
                                        <div className="font-semibold text-gray-800 truncate">{entry.nombre}</div>
                                        <div className="text-xs text-gray-500">{entry.deudas.length} deuda{entry.deudas.length === 1 ? '' : 's'} · pendientes: {pendientes.length}</div>
                                    </div>
                                </button>
                                <div className="flex items-center gap-3">
                                    <span className="text-lg font-bold text-red-600">${entry.total.toLocaleString('es-AR')}</span>
                                    {canManage && pendientes.length > 0 && (
                                        <button
                                            onClick={() => abrirCobrarTodas(entry.socioId, pendientes)}
                                            className="bg-green-600 text-white px-3 py-1.5 rounded text-sm hover:bg-green-700 font-medium"
                                        >
                                            Cobrar {pendientes.length > 1 ? `todas (${pendientes.length})` : ''}
                                        </button>
                                    )}
                                </div>
                            </div>

                            {isOpen && (
                                <div className="mt-4 border-t pt-3 space-y-2">
                                    {entry.deudas.map(d => (
                                        <div key={d.id} className="flex flex-wrap items-center justify-between gap-2 py-2 px-3 bg-gray-50 rounded">
                                            <div className="flex-1 min-w-0">
                                                <div className="text-sm text-gray-800">{d.descripcion || '(sin descripción)'}</div>
                                                <div className="text-xs text-gray-500">
                                                    {new Date(d.creado_en).toLocaleString('es-AR')} · {d.creado_por}
                                                    {d.estado === 'pagada' && d.pagada_en && (
                                                        <span className="ml-2 text-green-700">Pagada {new Date(d.pagada_en).toLocaleDateString('es-AR')} ({d.pagada_por})</span>
                                                    )}
                                                    {d.estado === 'cancelada' && d.cancelada_en && (
                                                        <span className="ml-2 text-gray-700">Cancelada {new Date(d.cancelada_en).toLocaleDateString('es-AR')} ({d.cancelada_por})</span>
                                                    )}
                                                </div>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <span className={`px-2 py-1 text-xs rounded-full font-semibold ${d.estado === 'pendiente' ? 'bg-yellow-100 text-yellow-800' : d.estado === 'pagada' ? 'bg-green-100 text-green-800' : 'bg-gray-200 text-gray-700'}`}>
                                                    {d.estado}
                                                </span>
                                                <span className="font-bold text-gray-900 w-24 text-right">${Number(d.monto).toLocaleString('es-AR')}</span>
                                                {d.estado === 'pendiente' && canManage && (
                                                    <button onClick={() => abrirCobrarUna(d)} className="text-green-700 hover:text-green-900 text-sm font-medium">Cobrar</button>
                                                )}
                                                {d.estado === 'pendiente' && canCancel && (
                                                    <button onClick={() => handleCancelar(d)} className="text-gray-500 hover:text-gray-700 text-sm">Cancelar</button>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>

            {cobrarModal && (
                <Modal isOpen={true} onClose={() => !procesando && setCobrarModal(null)} title="Cobrar deuda">
                    <div className="space-y-4">
                        {(!sesionCaja || sesionCaja.estado !== 'abierta') && (
                            <div className="bg-red-50 border border-red-200 rounded p-3 text-sm text-red-800">
                                Para registrar el cobro necesitás tener la <b>Caja abierta</b>. Abrila desde la pantalla "Caja".
                            </div>
                        )}
                        <div className="flex justify-between bg-yellow-50 border border-yellow-200 rounded p-3">
                            <span className="text-yellow-800">Total a cobrar</span>
                            <span className="text-2xl font-bold text-yellow-800">${cobrarModal.total.toLocaleString('es-AR')}</span>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Medio de pago</label>
                            <select
                                value={medioDePago}
                                onChange={e => setMedioDePago(e.target.value as MedioDePago)}
                                className="w-full p-2 border rounded"
                            >
                                <option value={MedioDePago.EFECTIVO}>Efectivo</option>
                                <option value={MedioDePago.TRANSFERENCIA}>Transferencia Bancaria</option>
                            </select>
                        </div>
                        <p className="text-xs text-gray-500">Se registra una venta en la caja abierta. Esta venta no genera puntos (los puntos del turno ya fueron acreditados cuando se marcó como pagado).</p>
                        <div className="flex justify-end gap-3 pt-2">
                            <button onClick={() => setCobrarModal(null)} disabled={procesando} className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 font-medium">Cancelar</button>
                            <button
                                onClick={confirmarCobro}
                                disabled={procesando || !sesionCaja || sesionCaja.estado !== 'abierta'}
                                className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 font-medium disabled:bg-gray-400"
                            >
                                {procesando ? 'Procesando...' : 'Confirmar cobro'}
                            </button>
                        </div>
                    </div>
                </Modal>
            )}
        </div>
    );
};

export default DeudasScreen;
